import { Outlet } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { CartDrawer } from '@/components/cart/CartDrawer';
import { PageTransition } from '@/components/layout/PageTransition';
import { MaintenancePage } from '@/components/system/MaintenancePage';
import { usePlatform } from '@/providers/PlatformProvider';
import { ComingSoonPage } from '@/pages/ComingSoonPage';

/**
 * Shell for every public page — BUILD-PLAN §25/§26/§56.
 *
 * Two structural details matter here:
 *
 *  1. `pb-bottom-nav lg:pb-0` reserves exactly the height of the fixed mobile
 *     navigation, so content is never hidden behind it — the §80 audit item
 *     "no content hidden behind bottom nav". The padding is removed at md,
 *     where the bottom bar is not rendered.
 *
 *  2. The skip link is the first focusable element on the page, letting
 *     keyboard and screen-reader users bypass navigation (§40).
 */
export function PublicLayout() {
  const { inMaintenance, isPreLaunch } = usePlatform();

  // Full maintenance replaces the entire shell — there is no navigation to
  // offer when nothing behind it is reachable.
  if (inMaintenance('full')) {
    return <MaintenancePage />;
  }

  return (
    /*
      The bottom-nav allowance belongs to the WHOLE page, not to <main>.
      ----------------------------------------------------------------------
      It used to sit on <main>, which clears the fixed mobile bar for page
      content — and then the footer, which renders after <main>, ran straight
      underneath it. The copyright line was permanently hidden on every phone
      and tablet, on every page of the site.
    */
    <div className="pb-bottom-nav flex min-h-screen flex-col bg-background lg:pb-0">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      {/*
        Pre-launch: the chrome is SHOWN but INERT — §24, §67.
        --------------------------------------------------------------------
        The company is previewing the shape of the finished site, so removing
        the navigation would hide the very thing they want to see. Instead it
        stays exactly as designed and stops being a control.

        `inert` is what makes that honest rather than cosmetic: it takes the
        whole subtree out of the tab order, hides it from assistive
        technology's interaction model and blocks pointer events — so a
        keyboard user cannot tab into a link that a mouse user cannot click.
        Disabling with `pointer-events-none` alone would leave every link
        reachable by Tab and Enter, which is precisely the kind of half-fix
        that lets someone through the front door.
      */}
      <div inert={isPreLaunch}>
        <Header />
      </div>

      <main id="main-content" className="flex-1">
        <PageTransition>
          {/*
            One destination while pre-launch, whatever the URL says. The
            router also redirects unknown paths here, so a shared deep link
            lands on the holding page rather than a 404.
          */}
          {isPreLaunch ? <ComingSoonPage /> : <Outlet />}
        </PageTransition>
      </main>

      <div inert={isPreLaunch}>
        <Footer />

        <BottomNavigation />
      </div>

      {/* Mounted once for the whole shell so any page can open it — but not
          before the shop has opened. */}
      {!isPreLaunch && <CartDrawer />}
    </div>
  );
}
